<template>
    <section class="mobile-rights-page">
        <van-nav-bar
            title="我的权益"
            left-arrow
            fixed
            placeholder
            z-index="100"
            safe-area-inset-top
            @click-left="goBack"
        />

        <main class="mobile-rights-main">
            <section class="mobile-rights-overview">
                <div>
                    <span>ACCOUNT BENEFITS</span>
                    <h1>我的产品权益</h1>
                    <p>查看个人订阅和已加入的企业授权。</p>
                    <div class="mobile-rights-overview__id">
                        <small>我的 ID</small>
                        <strong>{{ accountId || '—' }}</strong>
                    </div>
                </div>
                <van-icon name="shield-o" aria-hidden="true" />
            </section>

            <div v-if="loading" class="mobile-rights-loading">
                <van-loading type="spinner" color="#2478f2">正在加载权益</van-loading>
            </div>

            <div v-else-if="loadError" class="mobile-rights-error">
                <van-empty image="error" description="权益信息加载失败">
                    <van-button round type="info" size="small" @click="loadRights">重新加载</van-button>
                </van-empty>
            </div>

            <template v-else>
                <section class="mobile-rights-group">
                    <div class="mobile-rights-group__heading">
                        <div>
                            <h2>订阅版权限</h2>
                            <p>个人账号当前拥有的产品版本</p>
                        </div>
                        <span>{{ normalRights.length }} 项</span>
                    </div>

                    <div v-if="normalRights.length" class="mobile-rights-list">
                        <article
                            v-for="item in normalRights"
                            :key="`${item.username}-${item.level}-${item.start_time}`"
                            :class="[
                                'mobile-right-card',
                                'mobile-right-card--personal',
                                Number(item.level) === 2 ? 'mobile-right-card--vip' : 'mobile-right-card--basic',
                            ]"
                        >
                            <header class="mobile-right-card__header">
                                <div>
                                    <div class="mobile-right-card__eyebrow">
                                        <van-icon :name="Number(item.level) === 2 ? 'vip-card-o' : 'shield-o'" />
                                        <span>BIGEMAP PRO</span>
                                    </div>
                                    <div class="mobile-right-card__title-row">
                                        <h3>{{ levelLabel(item.level) }}</h3>
                                        <span v-if="Number(item.level) === 2" class="mobile-right-card__vip-mark">
                                            <van-icon name="star" /> VIP
                                        </span>
                                    </div>
                                </div>
                                <van-tag round :type="isExpired(item) ? 'danger' : 'success'">
                                    {{ isExpired(item) ? '已到期' : '有效' }}
                                </van-tag>
                            </header>

                            <div class="mobile-right-card__benefit-note">
                                <van-icon :name="Number(item.level) === 2 ? 'gem-o' : 'service-o'" />
                                <span>
                                    {{ Number(item.level) === 2 ? '付费订阅权益 · 专享高级功能' : '基础免费权益 · 可联系客服延期' }}
                                </span>
                            </div>

                            <div class="mobile-right-card__dates">
                                <div class="mobile-right-card__date-item">
                                    <div class="mobile-right-card__date-label">
                                        <van-icon name="clock-o" />
                                        <span>权益开始</span>
                                    </div>
                                    <strong>{{ formatTime(item.start_time) }}</strong>
                                </div>
                                <div class="mobile-right-card__date-item">
                                    <div class="mobile-right-card__date-label">
                                        <van-icon name="underway-o" />
                                        <span>权益截止</span>
                                    </div>
                                    <strong>{{ formatTime(item.end_time) }}</strong>
                                </div>
                            </div>

                            <div class="mobile-right-card__meta">
                                <span>注册时间</span>
                                <strong>{{ formatTime(item.create_time) }}</strong>
                            </div>
                        </article>
                    </div>

                    <div v-else class="mobile-rights-empty">
                        <van-icon name="coupon-o" />
                        <p>暂无个人订阅权益</p>
                    </div>
                </section>

                <section class="mobile-rights-group">
                    <div class="mobile-rights-group__heading">
                        <div>
                            <h2>我的企业</h2>
                            <p>企业授权及当前使用状态</p>
                        </div>
                        <span>{{ companyRights.length }} 家</span>
                    </div>

                    <div v-if="companyRights.length" class="mobile-rights-list">
                        <article v-for="company in companyRights" :key="company.mapping_id || company.net_id" class="mobile-right-card mobile-right-card--company">
                            <header class="mobile-right-card__header">
                                <div class="mobile-right-card__company-title">
                                    <div class="mobile-right-card__eyebrow">
                                        <van-icon name="diamond-o" />
                                        <span>COMPANY BENEFIT</span>
                                    </div>
                                    <h3>{{ company.company_name_alias || '未命名企业' }}</h3>
                                </div>
                                <van-tag v-if="company.is_default" round type="primary">当前使用</van-tag>
                                <van-tag v-else round plain>未加入</van-tag>
                            </header>

                            <div class="mobile-right-card__benefit-note mobile-right-card__benefit-note--company">
                                <van-icon name="gem-o" />
                                <span>企业付费授权 · 由所属企业提供</span>
                            </div>

                            <div class="mobile-right-card__company-info">
                                <div>
                                    <span>我的备注</span>
                                    <strong>{{ company.nickname || '未填写' }}</strong>
                                </div>
                            </div>

                            <div class="mobile-right-card__dates">
                                <div class="mobile-right-card__date-item">
                                    <div class="mobile-right-card__date-label">
                                        <van-icon name="clock-o" />
                                        <span>权益开始</span>
                                    </div>
                                    <strong>{{ formatTime(company.start_time) }}</strong>
                                </div>
                                <div class="mobile-right-card__date-item">
                                    <div class="mobile-right-card__date-label">
                                        <van-icon name="underway-o" />
                                        <span>权益截止</span>
                                    </div>
                                    <strong>{{ formatTime(company.end_time) }}</strong>
                                </div>
                            </div>

                            <div class="mobile-right-card__company-actions">
                                <van-button
                                    block
                                    round
                                    :plain="Boolean(company.is_default)"
                                    :type="company.is_default ? 'danger' : 'info'"
                                    :icon="company.is_default ? 'cross' : 'plus'"
                                    @click="toggleCompany(company)"
                                >
                                    {{ company.is_default ? '退出企业' : '加入企业' }}
                                </van-button>
                            </div>
                        </article>
                    </div>

                    <div v-else class="mobile-rights-empty">
                        <van-icon name="shop-o" />
                        <p>暂无企业权益</p>
                    </div>
                </section>
            </template>
        </main>
    </section>
</template>

<script>
export default {
    name: 'MobileUserRights',
    data() {
        return {
            baseUrl: '/bbs/pay/auth',
            loading: true,
            loadError: false,
            accountId: '',
            normalRights: [],
            companyRights: [],
        };
    },
    created() {
        this.loadRights();
    },
    methods: {
        goBack() {
            this.$router.push({path: '/user/profile/setting'});
        },
        loadRights() {
            this.loading = true;
            this.loadError = false;
            this.$bbs.get(this.baseUrl, {
                params: {
                    search: {},
                    sort: {},
                    total: 1,
                    pageSize: 15,
                    pageNumber: 1,
                },
                need: {error: true},
            }).then(response => {
                const data = response.data || {};
                const normal = Array.isArray(data.normal) ? data.normal : [];
                const company = Array.isArray(data.net) ? data.net : [];
                const account = normal.find(item => item.username) || company.find(item => item.username) || {};

                this.accountId = account.username || '';
                this.normalRights = normal.slice().sort((a, b) => Number(b.level) - Number(a.level));
                this.companyRights = company.map(item => ({
                    ...item,
                    company_name_alias: item.company && item.company.company_name_alias || item.company_name_alias,
                    start_time: item.company && item.company.start_time || item.start_time,
                    end_time: item.company && item.company.end_time || item.end_time,
                    sys_time: item.company && item.company.sys_time || item.sys_time,
                }));
            }).catch(() => {
                this.loadError = true;
            }).finally(() => {
                this.loading = false;
            });
        },
        levelLabel(level) {
            const levels = {
                1: '普通用户',
                2: '订阅版',
            };
            return levels[Number(level)] || '会员权益';
        },
        formatTime(value) {
            if (!value) {
                return '—';
            }
            if (typeof value === 'number' || /^\d{10,13}$/.test(String(value))) {
                const timestamp = Number(value) < 1000000000000 ? Number(value) * 1000 : Number(value);
                const date = new Date(timestamp);
                const pad = number => String(number).padStart(2, '0');
                return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
            }
            return String(value);
        },
        toTimestamp(value) {
            if (!value) {
                return NaN;
            }
            if (typeof value === 'number' || /^\d{10,13}$/.test(String(value))) {
                return Number(value) < 1000000000000 ? Number(value) * 1000 : Number(value);
            }
            return new Date(String(value).replace(/-/g, '/')).getTime();
        },
        isExpired(item) {
            if (!item.end_time || !item.sys_time) {
                return false;
            }
            const endTimestamp = this.toTimestamp(item.end_time);
            const systemTimestamp = this.toTimestamp(item.sys_time);
            return Number.isFinite(endTimestamp)
                && Number.isFinite(systemTimestamp)
                && endTimestamp < systemTimestamp;
        },
        toggleCompany(company) {
            const leaveCompany = Boolean(company.is_default);
            const companyName = company.company_name_alias || '该企业';

            this.$dialog.confirm({
                title: leaveCompany ? '退出企业' : '加入企业',
                message: leaveCompany
                    ? `退出“${companyName}”后将不再使用该企业提供的权益，确认继续吗？`
                    : `确认加入“${companyName}”并使用该企业提供的权益吗？`,
                confirmButtonText: leaveCompany ? '确认退出' : '确认加入',
                cancelButtonText: '取消',
                confirmButtonColor: leaveCompany ? '#df4d55' : '#6f57cf',
                closeOnClickOverlay: true,
            }).then(() => {
                this.$bbs.delete(this.baseUrl, {
                    data: {
                        id: company.mapping_id,
                        net_id: company.net_id,
                    },
                }).then(() => {
                    this.$toast.success(leaveCompany ? '已退出企业' : '已加入企业');
                    this.loadRights();
                });
            }).catch(() => {});
        },
    },
};
</script>

<style scoped lang="less">
.mobile-rights-page {
    min-height: 100vh;
    color: #182235;
    background: #f4f6f9;
}

.mobile-rights-page /deep/ .van-nav-bar__title {
    font-size: 17px;
    font-weight: 600;
}

.mobile-rights-page /deep/ .van-nav-bar--fixed {
    z-index: 100 !important;
    background: #fff;
    box-shadow: 0 1px 8px rgba(24, 34, 53, 0.06);
}

.mobile-rights-page /deep/ .van-nav-bar .van-icon {
    color: #28364b;
}

.mobile-rights-main {
    padding: 12px 12px 30px;
}

.mobile-rights-overview {
    position: relative;
    display: flex;
    overflow: hidden;
    min-height: 154px;
    padding: 22px 20px;
    box-sizing: border-box;
    align-items: center;
    justify-content: space-between;
    color: #fff;
    background:
        radial-gradient(circle at 92% 10%, rgba(87, 172, 255, 0.45), transparent 33%),
        linear-gradient(145deg, #0b234c, #1766c6);
    border-radius: 18px;
    box-shadow: 0 10px 28px rgba(27, 91, 174, 0.18);

    > div {
        position: relative;
        z-index: 1;
        max-width: 250px;
    }

    span {
        display: block;
        color: #88c6ff;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 1.4px;
    }

    h1 {
        margin: 6px 0 8px;
        font-size: 23px;
        font-weight: 700;
        line-height: 1.25;
    }

    p {
        margin: 0;
        color: rgba(255, 255, 255, 0.7);
        font-size: 14px;
        line-height: 1.5;
    }

    > .van-icon {
        color: rgba(255, 255, 255, 0.17);
        font-size: 76px;
        transform: rotate(-8deg);
    }
}

.mobile-rights-overview__id {
    display: flex;
    margin-top: 14px;
    align-items: center;
    gap: 10px;

    small {
        color: rgba(255, 255, 255, 0.65);
        font-size: 13px;
    }

    strong {
        color: #fff;
        font-size: 16px;
        font-weight: 650;
        letter-spacing: 0.2px;
    }
}

.mobile-rights-loading,
.mobile-rights-error {
    display: flex;
    min-height: 280px;
    align-items: center;
    justify-content: center;
}

.mobile-rights-group {
    margin-top: 20px;
}

.mobile-rights-group__heading {
    display: flex;
    min-height: 48px;
    padding: 0 4px 10px;
    align-items: flex-end;
    justify-content: space-between;

    h2 {
        margin: 0 0 4px;
        font-size: 17px;
        font-weight: 650;
    }

    p {
        margin: 0;
        color: #929cab;
        font-size: 13px;
    }

    > span {
        padding: 4px 9px;
        color: #667387;
        font-size: 12px;
        background: #e9edf3;
        border-radius: 999px;
    }
}

.mobile-rights-list {
    display: grid;
    gap: 12px;
}

.mobile-right-card {
    position: relative;
    overflow: hidden;
    padding: 19px 17px 17px;
    background: #fff;
    border: 1px solid rgba(225, 230, 238, 0.9);
    border-radius: 16px;
    box-shadow: 0 7px 24px rgba(29, 48, 76, 0.055);

    &::before {
        position: absolute;
        top: -52px;
        right: -38px;
        width: 130px;
        height: 130px;
        background: radial-gradient(circle, rgba(79, 139, 226, 0.13), transparent 68%);
        border-radius: 50%;
        content: '';
        pointer-events: none;
    }

    > * {
        position: relative;
        z-index: 1;
    }
}

.mobile-right-card--personal {
    .mobile-right-card__dates {
        margin-top: 15px;
    }
}

.mobile-right-card--basic {
    border-top: 3px solid #4d8edc;
    background: linear-gradient(155deg, #fff 54%, #f4f9ff 100%);
}

.mobile-right-card--vip {
    border-color: rgba(216, 179, 92, 0.42);
    border-top: 3px solid #d7a641;
    background: linear-gradient(150deg, #fffdf8 30%, #fff7e7 100%);
    box-shadow: 0 9px 28px rgba(157, 108, 26, 0.12);

    &::before {
        background: radial-gradient(circle, rgba(237, 190, 81, 0.25), transparent 68%);
    }

    .mobile-right-card__eyebrow,
    .mobile-right-card__eyebrow span {
        color: #b48125;
    }

    .mobile-right-card__benefit-note {
        color: #8b641e;
        background: rgba(235, 196, 105, 0.14);
        border-color: rgba(213, 169, 70, 0.2);
    }
}

.mobile-right-card--company {
    border-color: rgba(117, 94, 216, 0.25);
    border-top: 3px solid #775ed8;
    background: linear-gradient(155deg, #fff 42%, #f7f4ff 100%);
    box-shadow: 0 9px 28px rgba(76, 53, 160, 0.1);

    &::before {
        background: radial-gradient(circle, rgba(119, 94, 216, 0.18), transparent 68%);
    }
}

.mobile-right-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    > div {
        min-width: 0;
    }

    h3 {
        overflow: hidden;
        margin: 0;
        color: #202b3d;
        font-size: 20px;
        font-weight: 650;
        line-height: 1.35;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.mobile-right-card__eyebrow {
    display: flex;
    margin-bottom: 5px;
    align-items: center;
    color: #7f91a8;
    gap: 5px;

    .van-icon {
        font-size: 14px;
    }

    span {
        font-size: 10px;
        font-weight: 750;
        letter-spacing: 1.1px;
    }
}

.mobile-right-card__title-row {
    display: flex;
    align-items: center;
    gap: 9px;
}

.mobile-right-card__vip-mark {
    display: inline-flex;
    flex: none;
    padding: 3px 7px;
    align-items: center;
    color: #72501b;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.6px;
    background: linear-gradient(135deg, #ffe6a5, #d8a23c);
    border: 1px solid rgba(151, 102, 26, 0.16);
    border-radius: 999px;
    box-shadow: 0 3px 8px rgba(172, 117, 30, 0.16);

    .van-icon {
        margin-right: 3px;
        font-size: 9px;
    }
}

.mobile-right-card__benefit-note {
    display: flex;
    margin-top: 13px;
    padding: 9px 11px;
    align-items: center;
    color: #5f7490;
    font-size: 12px;
    font-weight: 550;
    background: rgba(77, 142, 220, 0.08);
    border: 1px solid rgba(77, 142, 220, 0.12);
    border-radius: 9px;
    gap: 7px;

    .van-icon {
        flex: none;
        font-size: 16px;
    }
}

.mobile-right-card__benefit-note--company {
    color: #675396;
    background: rgba(119, 94, 216, 0.08);
    border-color: rgba(119, 94, 216, 0.13);
}

.mobile-right-card__header /deep/ .van-tag {
    padding: 3px 8px;
    font-size: 12px;
}

.mobile-right-card__company-title {
    flex: 1;
}

.mobile-right-card__company-info {
    display: grid;
    margin: 12px 0 15px;
    padding: 11px 12px;
    grid-template-columns: 1fr;
    gap: 12px;
    background: #f7f6fc;
    border-radius: 10px;

    div {
        min-width: 0;
    }

    span,
    strong {
        display: block;
    }

    span {
        margin-bottom: 4px;
        color: #929bab;
        font-size: 12px;
    }

    strong {
        overflow: hidden;
        color: #3b4659;
        font-size: 14px;
        font-weight: 600;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.mobile-right-card__dates {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;

    .mobile-right-card__date-item {
        min-width: 0;
        padding: 11px;
        background: #fafbfc;
        border: 1px solid #edf0f4;
        border-radius: 10px;
    }

    > .mobile-right-card__date-item > strong {
        display: block;
        min-width: 0;
        margin-top: 8px;
        color: #475368;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.45;
        overflow-wrap: anywhere;
    }
}

.mobile-right-card__date-label {
    display: flex;
    align-items: center;
    color: #98a1af;
    gap: 7px;

    .van-icon {
        flex: none;
        color: #5d91d6;
        font-size: 16px;
    }

    span {
        font-size: 12px;
    }
}

.mobile-right-card__company-actions {
    margin-top: 15px;

    /deep/ .van-button {
        height: 42px;
        font-size: 14px;
        font-weight: 650;
        letter-spacing: 0.2px;
    }

    /deep/ .van-button--info {
        border-color: #735bd3;
        background: linear-gradient(135deg, #8069dd, #5f48bd);
        box-shadow: 0 6px 15px rgba(91, 67, 184, 0.2);
    }
}

.mobile-right-card__meta {
    display: flex;
    margin: 14px 2px 0;
    align-items: center;
    justify-content: space-between;

    span {
        color: #9aa3b1;
        font-size: 12px;
    }

    strong {
        color: #667185;
        font-size: 13px;
        font-weight: 500;
    }
}

.mobile-rights-empty {
    padding: 34px 0;
    color: #a3adbb;
    text-align: center;
    background: #fff;
    border: 1px dashed #dfe5ed;
    border-radius: 15px;

    .van-icon {
        font-size: 34px;
    }

    p {
        margin: 8px 0 0;
        font-size: 14px;
    }
}

@media (max-width: 380px) {
    .mobile-right-card__dates {
        grid-template-columns: 1fr;
    }
}
</style>
